import { useState } from "react";
import { holidayWindows, nextDay, type Holiday, type HoursOfOperationDetail, type ProfileField } from "@starsach/shared-types";
import { Hint } from "../../../components/Hint";
import { Card, DataTable, EmptyState, btnPrimary, btnSecondary, inputClass } from "../../../components/ui";
import { AnnouncementRef } from "../../customer/ProfileFieldEditors";
import { TabMenu, type CsvShape } from "./TabMenu";

type HoursField = Extract<ProfileField, { type: "hours" }>;

/** `YYYY-MM-DDTHH:MM`, or the same with a space - what a spreadsheet tends to write - and seconds it may add; returns the canonical form. */
function localDateTime(raw: string | undefined): string | undefined {
  const m = /^(\d{4}-\d{2}-\d{2})[T ]([01]\d|2[0-3]):([0-5]\d)(?::\d{2})?$/.exec((raw ?? "").trim());
  return m ? `${m[1]}T${m[2]}:${m[3]}` : undefined;
}

/** Midnight on the day of `local` (or today) to midnight the day after: the "all day" shortcut. */
export function allDay(local: string | undefined): Pick<Holiday, "from" | "to"> {
  const date = local && /^\d{4}-\d{2}-\d{2}/.test(local) ? local.slice(0, 10) : new Date().toISOString().slice(0, 10);
  return { from: `${date}T00:00`, to: `${nextDay(date)}T00:00` };
}

const byStart = (a: Holiday, b: Holiday) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to);

const holidayShape = (announcements: string[]): CsvShape<Holiday> => ({
  name: "holidays",
  headers: ["name", "from", "to", "announcement"],
  optional: ["announcement"],
  example: [
    { name: "Christmas", from: "2026-12-24T12:00", to: "2026-12-27T06:00", announcement: announcements[0] ?? "closed_christmas" },
    { name: "New Year's Day", from: "2027-01-01T00:00", to: "2027-01-02T00:00" },
  ],
  toRow: (h) => ({ name: h.name, from: h.from, to: h.to, announcement: h.announcement ?? "" }),
  fromRow: (r, line) => {
    const name = (r["name"] ?? "").trim();
    if (!name) return `Line ${line}: a holiday needs a name.`;
    const from = localDateTime(r["from"]);
    const to = localDateTime(r["to"]);
    if (!from) return `Line ${line}: from must be a date and time as YYYY-MM-DD HH:MM (got "${r["from"]}").`;
    if (!to) return `Line ${line}: to must be a date and time as YYYY-MM-DD HH:MM (got "${r["to"]}").`;
    if (to <= from) return `Line ${line}: "${name}" ends (${to}) before it starts (${from}).`;
    return { name, from, to, ...(r["announcement"] ? { announcement: r["announcement"] } : {}) };
  },
});

/**
 * Holidays, their own tab (the owner's ask of 2026-09-18), each a closed
 * window rather than a day (#265): from one date and time to another in
 * the schedule's zone - "close for Christmas at noon on the 24th, reopen
 * at six on the 27th" - with the announcement it plays, one per holiday,
 * chosen from the profile's announcements.
 */
export function HolidaysTab({ fields, announcements, connectOverrides, onUpdate }: { fields: HoursField[]; announcements: string[]; /** ADR-0019: the overrides Connect holds on each linked hours of operation, by schedule name - a source to copy from. */ connectOverrides?: Record<string, HoursOfOperationDetail["overrides"]>; onUpdate: (field: ProfileField, next: ProfileField) => void }) {
  const [which, setWhich] = useState(fields[0]?.name);
  const field = fields.find((f) => f.name === which) ?? fields[0];
  const [adding, setAdding] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");
  if (!field) {
    return (
      <Card title="Holidays">
        <EmptyState title="No hours of operation yet" hint="Holidays belong to a schedule. Add hours on the Hours tab first, then the holidays that close them." />
      </Card>
    );
  }
  const holidays = field.value.holidays ?? [];
  const setHolidays = (next: Holiday[]) => onUpdate(field, { ...field, value: { ...field.value, holidays: next } });
  const update = (i: number, patch: Partial<Holiday>) => setHolidays(holidays.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  const shape = holidayShape(announcements);
  // Where holidays can be copied from (the owner's ask of 2026-09-18: most hours of operation share them): another
  // schedule on this profile, or what Connect holds on this schedule's hours of operation. An override closed for
  // its whole span is one window; a single day open for part of it is read like the old part-day shape; anything
  // else (several days, each with hours) has no window shape and is left where it is.
  const fromConnect = (o: HoursOfOperationDetail["overrides"][number]): Holiday[] => {
    const name = o.name.replace(/^starsach:[^:]*:/, "");
    if (o.config.length === 0) return [{ name, from: `${o.from}T00:00`, to: `${nextDay(o.till)}T00:00` }];
    const entry = o.config[0];
    if (o.from !== o.till || o.config.length !== 1 || !entry) return [];
    return holidayWindows({ date: o.from, name, closed: false, opens: entry.start, closes: entry.end === "00:00" ? "23:59" : entry.end });
  };
  const sources: Array<{ id: string; label: string; holidays: Holiday[] }> = [
    ...fields.filter((f) => f.name !== field.name && (f.value.holidays?.length ?? 0) > 0).map((f) => ({ id: `schedule:${f.name}`, label: `schedule "${f.name}" (${f.value.holidays!.length})`, holidays: f.value.holidays! })),
    ...(field.value.connect_hours && connectOverrides?.[field.name]
      ? (() => {
          const windows = connectOverrides[field.name]!.flatMap(fromConnect);
          return windows.length > 0 ? [{ id: "connect", label: `Connect's overrides on ${field.value.connect_hours} (${windows.length})`, holidays: windows }] : [];
        })()
      : []),
  ];
  const merge = (base: Holiday[], incoming: Holiday[]) => {
    const byWindow = new Map(base.map((h) => [`${h.from}#${h.to}`, h]));
    for (const h of incoming) byWindow.set(`${h.from}#${h.to}`, h);
    return [...byWindow.values()].sort(byStart);
  };
  const copy = () => {
    const source = sources.find((s) => s.id === copyFrom);
    if (!source) return;
    // What is here already wins over the copy.
    setHolidays(merge(source.holidays, holidays));
    setCopyFrom("");
  };

  return (
    <Card title={fields.length > 1 ? `Holidays · ${field.name}` : "Holidays"}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {fields.length > 1 && (
          <label className="text-sm">
            <span className="mr-2 text-neutral-500">Schedule</span>
            <select className={`${inputClass} mt-0 w-auto`} value={field.name} onChange={(e) => setWhich(e.target.value)} aria-label="Which hours">
              {fields.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
            </select>
          </label>
        )}
        <button type="button" className={btnSecondary} onClick={() => setAdding((a) => !a)} aria-expanded={adding}>Add holiday</button>
        <Hint id="flow.holiday.window" label="what a holiday is" />
        {sources.length > 0 && (
          <span className="flex items-center gap-2 text-sm">
            <select className={`${inputClass} mt-0 w-auto`} value={copyFrom} aria-label="Copy holidays from" onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">Copy holidays from…</option>
              {sources.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <button type="button" className={`${btnSecondary} min-h-8 px-2.5 text-xs`} disabled={!copyFrom} onClick={copy}>Copy</button>
          </span>
        )}
        <span className="ml-auto">
          <TabMenu
            what="Holidays"
            shape={shape}
            items={holidays}
            onImport={(items, replace) => setHolidays(merge(replace ? [] : holidays, items))}
            onDeleteAll={() => setHolidays([])}
          />
        </span>
      </div>
      {adding && (
        <NewHoliday
          timeZone={field.value.timezone}
          announcements={announcements}
          onAdd={(h) => {
            setHolidays([...holidays, h].sort(byStart));
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      <DataTable
        caption={`Holidays on ${field.name}`}
        rows={holidays.map((h, i) => ({ ...h, i }))}
        rowKey={(h) => `${h.from}#${h.to}#${h.i}`}
        filter={holidays.length > 5}
        empty={<EmptyState title="No holidays yet" hint="Add one, or import a CSV - the example file shows the shape." />}
        columns={[
          { key: "name", header: "Name", render: (h) => <input className={`${inputClass} mt-0 w-44`} value={h.name} aria-label={`Holiday ${h.i + 1} name`} onChange={(e) => update(h.i, { name: e.target.value })} /> },
          { key: "from", header: <ZoneHeader label="From" timeZone={field.value.timezone} />, render: (h) => <input type="datetime-local" className={`${inputClass} mt-0 w-52`} value={h.from} aria-label={`Holiday ${h.i + 1} from`} onChange={(e) => update(h.i, { from: e.target.value })} /> },
          {
            key: "to",
            header: <ZoneHeader label="To" timeZone={field.value.timezone} />,
            render: (h) => (
              <span className="block">
                <input type="datetime-local" className={`${inputClass} mt-0 w-52`} value={h.to} aria-label={`Holiday ${h.i + 1} to`} aria-describedby={h.to <= h.from ? `holiday-${h.i}-problem` : undefined} onChange={(e) => update(h.i, { to: e.target.value })} />
                {h.to <= h.from && <span id={`holiday-${h.i}-problem`} role="alert" className="mt-1 block text-xs text-red-700 dark:text-red-300">Ends before it starts.</span>}
              </span>
            ),
          },
          { key: "allday", header: "", render: (h) => <button type="button" className={`${btnSecondary} min-h-7 px-2 text-xs`} aria-label={`Holiday ${h.i + 1} all day`} title="Midnight to midnight on the day it starts" onClick={() => update(h.i, allDay(h.from))}>All day</button> },
          { key: "announcement", header: "Announcement", render: (h) => <span className="block w-56"><AnnouncementRef value={h.announcement ?? ""} announcements={announcements} label={`Holiday ${h.i + 1} announcement`} onChange={(a) => update(h.i, { announcement: a || undefined })} /></span> },
          { key: "remove", header: "", render: (h) => <button type="button" className="text-xs text-red-700 underline dark:text-red-300" onClick={() => setHolidays(holidays.filter((_, j) => j !== h.i))}>Remove</button> },
        ]}
      />
      <p className="mt-3 text-xs text-neutral-500">
        Times are in the schedule's time zone, {field.value.timezone}; outside a holiday the weekly hours apply. A holiday with no announcement of its own plays the schedule's "say when closed".
        {field.value.connect_hours ? ` Each holiday is written into Connect's ${field.value.connect_hours} as an override named starsach:… on the next sync.` : ""}
      </p>
    </Card>
  );
}

/** #449: a holiday's times are the schedule's wall clock, not the reader's - said on the column, not only in the footnote. */
function ZoneHeader({ label, timeZone }: { label: string; timeZone: string }) {
  return (
    <>
      {label} <span className="font-normal normal-case tracking-normal">({zoneName(timeZone)})</span>
      <Hint id="flow.holiday.zone" label="which time zone" />
    </>
  );
}

const zoneName = (timeZone: string) => timeZone.replaceAll("_", " ");

function NewHoliday({ timeZone, announcements, onAdd, onCancel }: { timeZone: string; announcements: string[]; onAdd: (h: Holiday) => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const backwards = Boolean(from && to && to <= from);
  return (
    <form
      className="mb-4 flex flex-wrap items-end gap-3 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
      aria-label="New holiday"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim() || !from || !to || backwards) return;
        onAdd({ name: name.trim(), from, to, ...(announcement ? { announcement } : {}) });
      }}
    >
      <label className="text-xs">Name<input className={`${inputClass} w-48`} value={name} required placeholder="Christmas" onChange={(e) => setName(e.target.value)} /></label>
      <label className="text-xs">From ({zoneName(timeZone)})<input type="datetime-local" className={`${inputClass} w-52`} value={from} required onChange={(e) => setFrom(e.target.value)} /></label>
      <label className="text-xs">To ({zoneName(timeZone)})<input type="datetime-local" className={`${inputClass} w-52`} value={to} required aria-describedby={backwards ? "new-holiday-problem" : undefined} onChange={(e) => setTo(e.target.value)} /></label>
      <button type="button" className={`${btnSecondary} min-h-9 px-3 text-xs`} title="Midnight to midnight on the day it starts" onClick={() => { const d = allDay(from || to); setFrom(d.from); setTo(d.to); }}>All day</button>
      <span className="text-xs">Announcement<span className="block w-56"><AnnouncementRef value={announcement} announcements={announcements} label="New holiday announcement" onChange={setAnnouncement} /></span></span>
      <button type="submit" className={btnPrimary} disabled={backwards}>Add</button>
      <button type="button" className={btnSecondary} onClick={onCancel}>Cancel</button>
      {backwards && <p id="new-holiday-problem" role="alert" className="basis-full text-xs text-red-700 dark:text-red-300">The holiday ends before it starts.</p>}
    </form>
  );
}
